import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import hostelService from '../../services/hostelService';
import studentsService from '../../services/studentsService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, PencilSimple, Trash, Plus, ArrowLeft } from '@phosphor-icons/react';
import './hostel.css';

function VisitorLogsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('all');
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    hostel_id: '',
    student_id: '',
    visitor_name: '',
    visitor_phone: '',
    relationship: 'Parent',
    id_type: '',
    id_number: '',
    visit_date: new Date().toISOString().split('T')[0],
    check_in_time: '',
    check_out_time: '',
    notes: ''
  });

  const filterVisitors = React.useCallback(() => {
    let filtered = visitors;

    if (searchTerm) {
      filtered = filtered.filter(v =>
        v.visitor_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (relationshipFilter !== 'all') {
      filtered = filtered.filter(v => v.relationship === relationshipFilter);
    }

    setFilteredVisitors(filtered);
  }, [visitors, searchTerm, relationshipFilter]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterVisitors();
  }, [visitors, searchTerm, relationshipFilter, filterVisitors]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitorsData, studentsData, hostelsData] = await Promise.all([
        hostelService.listVisitors().catch(() => []),
        studentsService.listStudents().catch(() => []),
        hostelService.listHostels().catch(() => [])
      ]).catch(() => [[], [], []]);

      // Handle visitors response
      let visitorsArray = [];
      if (Array.isArray(visitorsData)) {
        visitorsArray = visitorsData;
      } else if (visitorsData.visitors) {
        visitorsArray = visitorsData.visitors;
      } else if (visitorsData.items) {
        visitorsArray = visitorsData.items;
      }

      // Handle students response
      let studentsArray = [];
      if (Array.isArray(studentsData)) {
        studentsArray = studentsData;
      } else if (studentsData.students) {
        studentsArray = studentsData.students;
      } else if (studentsData.items) {
        studentsArray = studentsData.items;
      }

      // Handle hostels response
      let hostelsArray = [];
      if (Array.isArray(hostelsData)) {
        hostelsArray = hostelsData;
      } else if (hostelsData.hostels) {
        hostelsArray = hostelsData.hostels;
      } else if (hostelsData.items) {
        hostelsArray = hostelsData.items;
      }

      setVisitors(visitorsArray);
      setStudents(studentsArray);
      setHostels(hostelsArray);

      // Set default hostel_id if hostels are available
      if (hostelsArray.length > 0 && !formData.hostel_id) {
        setFormData(prev => ({ ...prev, hostel_id: hostelsArray[0].id }));
      }
    } catch (error) {
      console.error('Error loading visitor logs:', error);
      toast.error('Failed to load visitor logs');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Unknown';
  };

  const handleEdit = (visitor) => {
    setEditingId(visitor.id);
    setFormData({
      hostel_id: visitor.hostel_id,
      student_id: visitor.student_id,
      visitor_name: visitor.visitor_name,
      visitor_phone: visitor.visitor_phone || '',
      relationship: visitor.relationship,
      id_type: visitor.id_type || '',
      id_number: visitor.id_number || '',
      visit_date: visitor.visit_date,
      check_in_time: visitor.check_in_time || '',
      check_out_time: visitor.check_out_time || '',
      notes: visitor.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await hostelService.deleteVisitor(deleteTarget);
      toast.success('Visitor record deleted successfully');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting visitor:', error);
      toast.error('Failed to delete visitor record');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.hostel_id || !formData.student_id || !formData.visitor_name || !formData.relationship) {
        toast.error('Please fill in all required fields');
        return;
      }

      const payload = {
        hostel_id: formData.hostel_id,
        student_id: formData.student_id,
        visitor_name: formData.visitor_name,
        visitor_phone: formData.visitor_phone,
        relationship: formData.relationship,
        id_type: formData.id_type || null,
        id_number: formData.id_number || null,
        visit_date: formData.visit_date,
        check_in_time: formData.check_in_time || null,
        check_out_time: formData.check_out_time || null,
        notes: formData.notes
      };

      if (editingId) {
        await hostelService.updateVisitor(editingId, payload);
        toast.success('Visitor record updated successfully');
      } else {
        await hostelService.createVisitor(payload);
        toast.success('Visitor log added successfully');
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({
        hostel_id: hostels.length > 0 ? hostels[0].id : '',
        student_id: '',
        visitor_name: '',
        visitor_phone: '',
        relationship: 'Parent',
        id_type: '',
        id_number: '',
        visit_date: new Date().toISOString().split('T')[0],
        check_in_time: '',
        check_out_time: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving visitor:', error);
      toast.error(editingId ? 'Failed to update visitor' : 'Failed to add visitor log');
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading visitor logs...</div></Layout>;
  }

  const relationships = ['Parent', 'Sibling', 'Friend', 'Guardian'];
  const visitorsWithCheckIn = visitors.filter(v => v.check_in_time).length;

  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Visitor Logs</h1>
              <p>Track guest visits and visitor information</p>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingId(null);
              setFormData({
                hostel_id: hostels.length > 0 ? hostels[0].id : '',
                student_id: '',
                visitor_name: '',
                visitor_phone: '',
                relationship: 'Parent',
                id_type: '',
                id_number: '',
                visit_date: new Date().toISOString().split('T')[0],
                check_in_time: '',
                check_out_time: '',
                notes: ''
              });
              setShowModal(true);
            }}
          >
            <Plus size={20} />
            Add Visitor Log
          </button>
        </div>

        <div className="stats-bar">
          <div className="stat">
            <h3>Total Visitors</h3>
            <p className="stat-value">{visitors.length}</p>
          </div>
          <div className="stat">
            <h3>Checked In</h3>
            <p className="stat-value">{visitorsWithCheckIn}</p>
          </div>
          <div className="stat">
            <h3>Unique Visitors</h3>
            <p className="stat-value">{new Set(visitors.map(v => v.visitor_name)).size}</p>
          </div>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search visitors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={relationshipFilter}
            onChange={(e) => setRelationshipFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Relationships</option>
            {relationships.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="table-container">
          {filteredVisitors.length === 0 ? (
            <div className="empty-state">
              <p>No visitor logs found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Visitor Name</th>
                  <th>Relationship</th>
                  <th>Student</th>
                  <th>Visit Date</th>
                  <th>Check-in Time</th>
                  <th>Check-out Time</th>
                  <th>Phone</th>
                  <th>ID Verification</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVisitors.map((visitor) => (
                  <tr key={visitor.id}>
                    <td>{visitor.visitor_name}</td>
                    <td className="text-capitalize">{visitor.relationship}</td>
                    <td>{getStudentName(visitor.student_id)}</td>
                    <td>{visitor.visit_date}</td>
                    <td>{visitor.check_in_time || '-'}</td>
                    <td>{visitor.check_out_time || '-'}</td>
                    <td>{visitor.visitor_phone || '-'}</td>
                    <td>{visitor.id_type ? `${visitor.id_type}${visitor.id_number ? ` · ${visitor.id_number}` : ''}` : '-'}</td>
                    <td>{visitor.notes || '-'}</td>
                    <td className="action-buttons">
                      <button
                        className="btn-icon btn-primary"
                        title="Edit"
                        onClick={() => handleEdit(visitor)}
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => handleDelete(visitor.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Visitor Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Visitor' : 'Add Visitor Log'}</h2>
              <button 
                type="button" 
                className="btn-close"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {!editingId ? (
                <>
                  <div className="form-group">
                    <label>Hostel *</label>
                    <select
                      name="hostel_id"
                      value={formData.hostel_id}
                      onChange={(e) => setFormData({ ...formData, hostel_id: e.target.value })}
                      required
                    >
                      <option value="">Select Hostel</option>
                      {hostels.map(h => (
                        <option key={h.id} value={h.id}>
                          {h.hostel_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Student *</label>
                    <select
                      name="student_id"
                      value={formData.student_id}
                      onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                      required
                    >
                      <option value="">Select Student</option>
                      {students.map(s => (
                        <option key={s.id} value={s.student_id}>
                          {s.first_name} {s.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Visitor Name *</label>
                    <input
                      type="text"
                      value={formData.visitor_name}
                      onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })}
                      placeholder="e.g., John Smith"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Relationship *</label>
                    <select
                      name="relationship"
                      value={formData.relationship}
                      onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                      required
                    >
                      <option value="Parent">Parent</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Friend">Friend</option>
                      <option value="Guardian">Guardian</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={formData.visitor_phone}
                      onChange={(e) => setFormData({ ...formData, visitor_phone: e.target.value })}
                      placeholder="e.g., +233501234567"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>ID Type</label>
                      <select
                        value={formData.id_type}
                        onChange={(e) => setFormData({ ...formData, id_type: e.target.value })}
                      >
                        <option value="">Not verified</option>
                        <option value="National ID">National ID</option>
                        <option value="Passport">Passport</option>
                        <option value="Driver's License">Driver's License</option>
                        <option value="Voter ID">Voter ID</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>ID Number</label>
                      <input
                        type="text"
                        value={formData.id_number}
                        onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                        placeholder="e.g., GHA-123456789-0"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Visit Date *</label>
                      <input
                        type="date"
                        value={formData.visit_date}
                        onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Check-in Time</label>
                      <input
                        type="time"
                        value={formData.check_in_time}
                        onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Check-out Time</label>
                    <input
                      type="time"
                      value={formData.check_out_time}
                      onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any additional notes..."
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label><strong>Hostel:</strong> {hostels.find(h => h.id === formData.hostel_id)?.hostel_name || 'Unknown'}</label>
                  </div>

                  <div className="form-group">
                    <label><strong>Student:</strong> {getStudentName(formData.student_id)}</label>
                  </div>

                  <div className="form-group">
                    <label>Visitor Name</label>
                    <input
                      type="text"
                      value={formData.visitor_name}
                      onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })}
                      placeholder="e.g., John Smith"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Relationship *</label>
                    <select
                      name="relationship"
                      value={formData.relationship}
                      onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                      required
                    >
                      <option value="Parent">Parent</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Friend">Friend</option>
                      <option value="Guardian">Guardian</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Visit Date</label>
                    <input
                      type="date"
                      value={formData.visit_date}
                      onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={formData.visitor_phone}
                      onChange={(e) => setFormData({ ...formData, visitor_phone: e.target.value })}
                      placeholder="e.g., +233501234567"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>ID Type</label>
                      <select
                        value={formData.id_type}
                        onChange={(e) => setFormData({ ...formData, id_type: e.target.value })}
                      >
                        <option value="">Not verified</option>
                        <option value="National ID">National ID</option>
                        <option value="Passport">Passport</option>
                        <option value="Driver's License">Driver's License</option>
                        <option value="Voter ID">Voter ID</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>ID Number</label>
                      <input
                        type="text"
                        value={formData.id_number}
                        onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                        placeholder="e.g., GHA-123456789-0"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Check-in Time</label>
                      <input
                        type="time"
                        value={formData.check_in_time}
                        onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Check-out Time</label>
                      <input
                        type="time"
                        value={formData.check_out_time}
                        onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any additional notes..."
                    />
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Update' : 'Add'} Visitor
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default VisitorLogsPage;
